export enum HeaderKeys {
  ContentTypeKey = 'Content-Type',
  Accept = 'Accept',
  Authorization = 'Authorization',
  Bearer = 'Bearer',
}
export enum SideMenuItems {
  Dashboard = 'Dashboard',
  AllPatients = 'All patients',
  AdminUsers = 'Admin users',
  Templates = 'Admin templates',
  UnlockRequests = 'Unlock requests',
  VisitRuns = 'Visit runs',
  AppVersion = 'App version',
  FormsExport = 'Forms export',
}

export enum YesNoAnswer {
  Yes = 'Yes',
  No = 'No',
}
export enum YesNoAnswerSwedish {
  Yes = 'Ja',
  No = 'Nej',
}

export enum MigrainesShortEnum {
  Aura = 'A',
  Migraine = 'M',
  Horton = 'HO',
  Huvuduvark = 'HU',
  Tension = 'T',
  AnnanHuvuduvark = 'AH',
  AuraUtanHuvuduvark = 'AUH',
  IngenHuvuduvark = 'IH',
  Lakemedel = 'L',
  Mens = 'ME',
}

export enum MigrainesEnum {
  Aura = 'Aura',
  Migraine = 'Migrän',
  Horton = 'Horton',
  Huvuduvark = 'Huvudvärk',
  Tension = 'Spänningshuvudvärk',
  AnnanHuvuduvark = 'Annan huvudvärk',
  AuraUtanHuvuduvark = 'Aura utan huvudvärk',
  IngenHuvuduvark = 'Ingen Huvudvärk',
  Lakemedel = 'Läkemedel',
  Mens = 'Mens',
}
export interface MediaBreakPoints {
  mobile: boolean;
  smallScreen: boolean;
  mobileLandscape: boolean;
  smallScreenPortrait: boolean;
  mediumScreen: boolean;
  bigScreen: boolean;
  portrait: boolean;
  landscape: boolean;
  tablet: boolean;
  desktop: boolean;
}

export type Profession = 'nurse' | 'doctor' | 'neurologist' | 'administrator';

export const professions: Profession[] = [
  'nurse',
  'doctor',
  'neurologist',
  'administrator',
];

export type SignupType =
  | 'selfSignup'
  | 'legacy'
  | 'referralPrimaryCare'
  | 'referralSpecialistCare'
  | 'adminWithPatientAccess'
  | 'demo';

export const signupTypes: SignupType[] = [
  'selfSignup',
  'legacy',
  'referralPrimaryCare',
  'referralSpecialistCare',
  'adminWithPatientAccess',
  'demo',
];

export const PERMISSIONS = [
  'admin:add',
  'admin:delete',
  'patient:delete',
  'template:writeTemplate',
  'technicalAdmin',
  'data:export',
] as const;

export type Permission = (typeof PERMISSIONS)[number];
export interface IAddUser {
  email: string;
  firstName: string;
  lastName: string;
  personalNumber: string;
  phoneNumber: string;
  isAdmin: boolean;
  profession: Profession;
}

export interface IAddPatient {
  personalNumber: string;
  signupType: SignupType;
  isAdmin: boolean;
  isPatient: boolean;
}

export interface IJwtPayload {
  iss?: string;
  nbf?: number;
  iat: number;
  exp: number;
  aud?: string;
  _id?: string;
  isAdmin: boolean;
  isPatient: boolean;
}

export interface IUser {
  _id: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  personalNumber: string;
  isAdmin: boolean;
  isPatient: boolean;

  pushNotificationToken?: string;
  pushNotificationTime?: string;
  pushNotificationEnabled?: string;
  onboardingCompleted?: boolean;
  isMenstruating?: boolean;
  profession?: Profession;
  signupType?: SignupType;
  appointmentCreated?: boolean;
  formSubmitted?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  isSuperAdmin?: boolean;
  permissions?: Permission[];
  priority?: 'P1' | 'P2' | 'P3' | null;
}

export interface ICountUsersResponse {
  numberOfPatients?: number;
  numberOfAdmins?: number;
  numberOfSubmissionRequests?: number;
}
export interface IAnswer {
  value: string;
  text: string;
}

export interface IDiaryEntry {
  _id: string;
  diaryId: string;
  answers: Array<{
    questionId: string;
    order: number;
    answer: IAnswer[];
  }>;
  createdAt: string;
  updatedAt: string;
  diaryDate: string;
  __v: number;
}

export interface IDateRange {
  startDate: string;
  endDate: string;
}

export interface ICalendarEvent {
  id: number;
  start: Date;
  end: Date;
  entries: string[];
  title?: string;
}

export interface ICalendarState {
  diaryEntries: IDiaryEntry[] | null;
  startDate: Date | null;
  endDate: Date | null;
}
export interface ICalendarStatisticsState {
  statisticsEntries: IDiaryEntry[] | null;
  statisticsLoader: boolean | null;
  exportButtonLoader: boolean;
  file: File | null;
}
export interface IDateRangeStatistics {
  numberOfDays?: number;
  numberOfDaysWithHeadaches: number;
  numberOfMigraines: number;
  numberOfTensionHeadaches: number;
  numberOfHortons: number;
  numberOfOtherHeadaches: number;
  numberOfAuras: number;
  numberOfAurasWithoutHeadaches: number;
  numberOfWithoutHeadaches: number;
  numberOfLakemedels: number;
  numberOfMens: number;

  totalSumOfIntensitiesHeadaches: number;
  totalSumOfIntensitiesTensionHeadaches: number;
  totalSumOfIntensitiesMigraines: number;
  totalSumOfIntensitiesHortons: number;
  totalSumOfIntensitiesOtherHeadaches: number;
}

export interface IChartDot {
  x: string;
  y?: number;
}
export interface IDailyStatistics {
  headaches: IChartDot[];
  tesnionHeadaches: IChartDot[];
  migraines: IChartDot[];
  hortons: IChartDot[];
  otherHeadaches: IChartDot[];
}

export enum AlertType {
  Success = 'success',
  Neutral = 'neutral',
  Warning = 'warning',
  Danger = 'danger',
}

export interface IGetPatientsQuery {
  isAdmin?: boolean;
  isPatient?: boolean;
}

export interface IMessage {
  _id: string;
  senderId?: string | null;
  senderName?: string;
  senderProfession?: Profession;
  receiverId: string;
  content: string;
  isSystemMessage?: boolean;
  readAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  requiresConfirmation?: boolean;
  confirmationAt?: string | Date | null;
  confirmationDueAt?: string | Date | null;
  autoReminderAfterMs?: number | null;
}

export interface IMessageBoardState {
  messages: IMessage[];
  messageContent: string;
}

export type TemplateListItem = {
  _id: string;
  name: string;
  category?: string | null;
  language?: string;
  status?: string;
  currentVersion?: number;
  createdAt?: string;
  updatedAt?: string;
  bodyMarkdown?: string;
};

export interface TemplateVersion {
  v: number;
  bodyMarkdown: string;
  placeholders?: Array<{ key: string; required?: boolean; source?: string }>;
  updatedAt?: string;
}

export interface TemplateDoc extends TemplateListItem {
  tags?: string[];
  status: 'active' | 'archived';
  versions: TemplateVersion[];
  createdAt?: string;
  updatedAt?: string;
}

export interface TemplatesState {
  items: TemplateListItem[];
  selectedTemplateId: string | null;
  selectedTemplateBody: string;
  loading: boolean;
  error: string | null;
}

export interface CreateTemplatePayload {
  name: string;
  bodyMarkdown: string;
  category?: string | null;
  language?: string;
  tags?: string[];
  placeholders?: Array<{ key: string; required?: boolean; source?: string }>;
  updatedBy?: { userId?: string; name?: string };
}

export interface CreateTemplateResponse {
  id: string;
}

export interface UpdateTemplateBody {
  name?: string;
  bodyMarkdown: string;
}
export interface UpdateTemplatePayload {
  id: string;
  body: UpdateTemplateBody;
}
